/** Invisible characters that tag along when text is copied from Word or Slack. */
const INVISIBLE_CODE_POINTS = new Set([0x200b, 0x200c, 0x200d, 0x2060, 0xfeff]);

/** Unusual space characters, such as the one Word inserts instead of a space. */
const UNICODE_SPACE_CODE_POINTS = new Set([
  0x00a0, 0x1680, 0x202f, 0x205f, 0x3000,
]);

const isUnicodeSpace = (codePoint: number): boolean =>
  UNICODE_SPACE_CODE_POINTS.has(codePoint) ||
  (codePoint >= 0x2000 && codePoint <= 0x200a);

const isControl = (codePoint: number): boolean =>
  codePoint <= 0x1f || codePoint === 0x7f;

/** Bullet characters people paste in front of each line of a list. */
const LIST_MARKER_CODE_POINTS = new Set([
  0x2022, 0x25e6, 0x2023, 0x25aa, 0x25cf, 0x00b7, 0x2013, 0x2014, 0x002a,
  0x003e,
]);

/** A line number on its own, such as `1.`, `2)` or `a)`. */
const LIST_NUMBER = /^[0-9a-z]{1,4}[.)]$/i;

/** The address part of `Display Name <addr@x.com>`. */
const BRACKETED_ADDRESS = /<([^<>]*)>/g;

/** What separates one entry from the next. Spaces only split inside an entry. */
const ENTRY_SEPARATORS = /[,;\n\r]+/;
const WHITESPACE = /\s+/;

const hasAlphanumeric = (token: string): boolean => /[\p{L}\p{N}]/u.test(token);

const looksLikeAddress = (entry: string): boolean =>
  entry.includes("@") || entry.includes("<");

/** Removes leading bullets so `*addr@x.com` still works. */
const stripListMarker = (token: string): string => {
  let index = 0;
  while (index < token.length) {
    const codePoint = token.codePointAt(index);
    if (codePoint === undefined || !LIST_MARKER_CODE_POINTS.has(codePoint))
      break;
    index += String.fromCodePoint(codePoint).length;
  }
  return token.slice(index).trim();
};

/**
 * True for leftovers from a pasted list, like a bullet or a line number.
 * Calling these invalid would bury the addresses that really are wrong.
 */
const isListLeftover = (token: string): boolean =>
  !hasAlphanumeric(token) || LIST_NUMBER.test(token);

/** RFC 5321 / RFC 1035 practical limits. */
const MAX_LOCAL_LENGTH = 64;
const MAX_TOTAL_LENGTH = 254;
const MAX_LABEL_LENGTH = 63;

/**
 * The part before the `@`. ASCII only.
 *
 * Mailchimp Transactional (Mandrill) refuses accented and non-Latin characters
 * before the `@` but allows them in the domain. Accepting them here would save
 * an invite that is never delivered. See
 * https://mailchimp.com/help/international-characters-in-mailchimp/
 */
const LOCAL_PART = "[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+";

/** One piece of a domain, such as `henkel` or `co` in `henkel.co.uk`. */
const DOMAIN_LABEL = "[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])?";

const EMAIL_PATTERN = new RegExp(
  `^${LOCAL_PART}@${DOMAIN_LABEL}(?:\\.${DOMAIN_LABEL})+$`,
  "u",
);

export interface ParsedEmailList {
  /** Addresses we can send to, tidied up and with duplicates removed. */
  valid: string[];
  /**
   * Text we could not read as an address, in the order it appeared. Spaces and
   * bullets are removed first, so it is close to what the user typed but not
   * always an exact copy.
   */
  invalid: string[];
}

/**
 * Removes invisible characters and turns odd spaces into normal ones, so two
 * addresses that look the same are treated as the same.
 */
const cleanInvisibles = (input: string): string => {
  let cleaned = "";
  for (const character of input) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (INVISIBLE_CODE_POINTS.has(codePoint)) continue;
    cleaned +=
      isControl(codePoint) || isUnicodeSpace(codePoint) ? " " : character;
  }
  return cleaned.normalize("NFC");
};

/**
 * Says whether an address we have already tidied up can receive email.
 * @param email - the address, expected trimmed and lowercased
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.length > MAX_TOTAL_LENGTH) return false;
  if (!EMAIL_PATTERN.test(email)) return false;

  const atIndex = email.lastIndexOf("@");
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);

  if (local.length > MAX_LOCAL_LENGTH) return false;
  // A dot cannot start, end, or repeat on either side of the @.
  if (`${local}.${domain}`.includes("..")) return false;
  if (local.startsWith(".") || local.endsWith(".")) return false;
  if (domain.split(".").some((label) => label.length > MAX_LABEL_LENGTH))
    return false;
  // An all-number ending like `user@host.123` is never a real domain.
  const tld = domain.slice(domain.lastIndexOf(".") + 1);
  return !/^\p{N}+$/u.test(tld);
};

/**
 * Turns one piece of text into an address we can send to, handling `mailto:`
 * prefixes and stray quotes or angle brackets.
 * @returns the tidied address, or null when it cannot be used
 */
export const normalizeEmail = (raw: string): string | null => {
  if (typeof raw !== "string") return null;

  const candidate = cleanInvisibles(raw)
    .trim()
    .replace(/^mailto:/i, "")
    .replace(/^[<"']+|[>"']+$/g, "")
    .trim()
    .toLowerCase();

  return isValidEmail(candidate) ? candidate : null;
};

/**
 * True when an entry is part of the name in front of the next address.
 *
 * Outlook copies contacts as `Doe, John <john@x.com>`. The comma splits the
 * name in two, and `Doe` would otherwise be reported as a failed address.
 */
const isNamePart = (entries: string[], index: number): boolean => {
  if (looksLikeAddress(entries[index] ?? "")) return false;
  // Only the entry directly before the address counts, so a typo further up
  // the list is still reported instead of being read as part of a name.
  return (entries[index + 1] ?? "").includes("<");
};

/**
 * Reads free-typed or pasted text and returns the addresses we can send to.
 *
 * Entries are separated by commas, semicolons or new lines, and by spaces
 * within an entry. Handles `Name <addr>` and `mailto:` forms, bulleted and
 * numbered lists, and hidden characters from a copy-paste. Addresses are
 * lowercased and de-duplicated. Anything left over comes back under `invalid`
 * so the caller can show the user what went wrong.
 */
export const parseEmailList = (input: string): ParsedEmailList => {
  const valid: string[] = [];
  const invalid: string[] = [];

  if (typeof input !== "string" || !input.trim()) return { valid, invalid };

  const seen = new Set<string>();

  const collect = (candidate: string) => {
    const token = stripListMarker(candidate.trim());
    if (!token || isListLeftover(token)) return;

    const normalized = normalizeEmail(token);
    if (!normalized) {
      if (!invalid.includes(token)) invalid.push(token);
      return;
    }
    if (seen.has(normalized)) return;

    seen.add(normalized);
    valid.push(normalized);
  };

  const entries = cleanInvisibles(input).split(ENTRY_SEPARATORS);

  entries.forEach((entry, index) => {
    if (entry.includes("<")) {
      // Take what is inside the brackets, then any plain address beside it.
      const addresses: string[] = [];
      const rest = entry.replace(BRACKETED_ADDRESS, (_match, address) => {
        addresses.push(address);
        return " ";
      });

      addresses.forEach(collect);
      for (const token of rest.split(WHITESPACE))
        if (token.includes("@")) collect(token);
      return;
    }

    if (isNamePart(entries, index)) return;

    for (const token of entry.split(WHITESPACE)) collect(token);
  });

  return { valid, invalid };
};
